/* Editable spreadsheet component — renders rows grouped by sheet. */
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Icon } from './icons.jsx';
import { DEFAULT_VOCAB, roleOf, labelsOf } from './status-vocab.js';

// Export writers live in a JSX-free module so they are testable in plain Node.
export { exportXlsx, exportToOriginalWorkbook } from './xlsx-export.js';

export function Cell({ value, onChange, multiline = true, className = '', readOnly = false }) {
  const ref = useRef(null);
  const [local, setLocal] = useState(value ?? '');
  const fit = () => {
    const el = ref.current; if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.max(32, el.scrollHeight) + 'px';
  };
  useEffect(() => { setLocal(value ?? ''); }, [value]);
  useEffect(() => { fit(); }, [local]);
  return (
    <textarea
      ref={ref}
      className={'cell-edit ' + className}
      value={local}
      readOnly={readOnly}
      rows={1}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => { if (local !== value) onChange(local); }}
      onKeyDown={(e) => {
        if (e.key === 'Enter' && !e.shiftKey && !multiline) {
          e.preventDefault(); e.target.blur();
        }
        if (e.key === 'Escape') { setLocal(value); e.target.blur(); }
      }}
    />
  );
}

export function StatusBadge({ status, vocab = DEFAULT_VOCAB }) {
  const cls = roleOf(vocab, status);
  return (
    <span className={'badge ' + cls}>
      <span className="dot"></span>
      {status}
    </span>
  );
}

export function ConfMeter({ value }) {
  const pct = Math.round((value || 0) * 100);
  const color = pct >= 80 ? 'var(--success)' : pct >= 55 ? '#f59e0b' : 'var(--err)';
  return (
    <div className="conf-wrap">
      <div className="conf-track"><div className="conf-fill" style={{ width: pct + '%', background: color }}/></div>
      <span className="conf-label">{pct}%</span>
    </div>
  );
}

/**
 * Spreadsheet grouped by sheet tabs — like a real Excel with bottom tabs.
 * rows: [{id, sheet, question, answer, status, confidence, edited?}]
 */
export function Spreadsheet({ rows, setRows, vocab = DEFAULT_VOCAB }) {
  const sheets = useMemo(() => {
    const seen = new Set();
    const order = [];
    rows.forEach(r => { if (!seen.has(r.sheet)) { seen.add(r.sheet); order.push(r.sheet); } });
    return order;
  }, [rows]);
  const [activeSheet, setActiveSheet] = useState(sheets[0] || null);
  const [expanded, setExpanded] = useState(() => new Set());
  useEffect(() => {
    if (!activeSheet || !sheets.includes(activeSheet)) setActiveSheet(sheets[0] || null);
  }, [sheets.join('|')]);

  const sheetRows = useMemo(() => rows.filter(r => r.sheet === activeSheet), [rows, activeSheet]);

  const toggleExpanded = (id) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const updateRow = (id, patch) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch, edited: true } : r));
  };

  return (
    <div className="flex-col" style={{ minHeight: 0, flex: 1 }}>
      <div className="sheet-toolbar">
        <Icon name="sheet" size={14} color="var(--text-3)"/>
        <span style={{ fontWeight: 500, color: 'var(--text-2)' }}>Edit inline — changes apply to the exported file</span>
        <div className="spacer"/>
        <span className="sheet-hint">{rows.length} rows · {sheets.length} sheets</span>
      </div>
      <div className="sheet-scroll">
        <table className="sheet-table">
          <colgroup>
            <col style={{ width: 36 }}/>
            <col style={{ width: '34%' }}/>
            <col style={{ width: '40%' }}/>
            <col style={{ width: 110 }}/>
            <col style={{ width: 110 }}/>
          </colgroup>
          <thead>
            <tr>
              <th className="col-idx">#</th>
              <th>Requirement</th>
              <th>Response</th>
              <th>Status</th>
              <th>Confidence</th>
            </tr>
          </thead>
          <tbody>
            <tr className="row-section">
              <td className="col-idx"></td>
              <td colSpan={4} style={{ padding: '6px 14px' }}>
                <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, letterSpacing: '.04em', color: 'var(--dark-text)', textTransform: 'uppercase' }}>
                  {activeSheet}
                </div>
              </td>
            </tr>
            {sheetRows.map((r, i) => {
              const isOpen = expanded.has(r.id);
              const hasDetails = r.status_rationale || r.confidence_rationale || (r.citations && r.citations.length);
              return (
                <React.Fragment key={r.id}>
                  <tr className={'row-question status-' + roleOf(vocab, r.status) + (isOpen ? ' expanded' : '')}>
                    <td className="col-idx">
                      <div className="idx-stack">
                        <span>{i + 1}</span>
                        {hasDetails && (
                          <button
                            className={'why-toggle' + (isOpen ? ' open' : '')}
                            onClick={() => toggleExpanded(r.id)}
                            title={isOpen ? 'Hide reasoning' : 'Show reasoning'}
                          >
                            ▸
                          </button>
                        )}
                      </div>
                    </td>
                    <td>
                      <Cell value={r.question} onChange={(v) => updateRow(r.id, { question: v })}/>
                    </td>
                    <td>
                      <Cell value={r.answer} onChange={(v) => updateRow(r.id, { answer: v })}/>
                    </td>
                    <td className="sheet-status-cell" style={{ verticalAlign: 'middle' }}>
                      <select
                        value={r.status}
                        onChange={(e) => updateRow(r.id, { status: e.target.value })}
                        className={'status-select status-' + roleOf(vocab, r.status)}
                      >
                        {labelsOf(vocab).map((l) => <option key={l}>{l}</option>)}
                      </select>
                      {r.edited && <span className="edit-flag" title="Edited since run"></span>}
                    </td>
                    <td style={{ verticalAlign: 'middle', padding: '8px 12px' }}>
                      <ConfMeter value={r.confidence}/>
                    </td>
                  </tr>
                  {isOpen && hasDetails && (
                    <tr className="row-detail">
                      <td className="col-idx"></td>
                      <td colSpan={4}>
                        <div className="detail-grid">
                          {r.status_rationale && (
                            <div className="detail-block">
                              <div className="detail-label">Why <StatusBadge status={r.status}/></div>
                              <div className="detail-body">{r.status_rationale}</div>
                            </div>
                          )}
                          {r.confidence_rationale && (
                            <div className="detail-block">
                              <div className="detail-label">
                                Why {Math.round((r.confidence || 0) * 100)}% confident
                              </div>
                              <div className="detail-body">{r.confidence_rationale}</div>
                            </div>
                          )}
                          {r.citations && r.citations.length > 0 && (
                            <div className="detail-block citations">
                              <div className="detail-label">RKB citations</div>
                              <div className="detail-body">
                                {r.citations.map((c, idx) => (
                                  <code key={idx} className="cite-chip">{c}</code>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="sheet-toolbar" style={{ borderTop: '1px solid var(--border)', borderBottom: 'none' }}>
        <div className="sheet-tabs">
          {sheets.map(s => (
            <button
              key={s}
              className={'sheet-tab' + (s === activeSheet ? ' active' : '')}
              onClick={() => setActiveSheet(s)}
            >
              {s}
              <span style={{ marginLeft: 6, color: 'var(--text-3)', fontFamily: 'var(--font-mono)', fontSize: 10.5 }}>
                {rows.filter(r => r.sheet === s).length}
              </span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
